import { Injectable, Injector, inject } from '@angular/core';
import { AuthService } from '../shared/services/auth.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, ReplaySubject, Subject, of } from 'rxjs';
import { CanActivateFn } from '@angular/router';
import { environment } from '../../environments/environment';
import { Ipdv } from '../shared/models/pdvResponse';
import { catchError, filter, finalize, map, take } from 'rxjs/operators';
import { allSessionStorage } from '../shared/constantes';
import { FirebaseService } from './firebase.service';

export interface CommissionsOpenPayload {
    responseCommissions: any;
}

export interface ContractConditionsOpenPayload {
    responseContractConditions: any;
}

export interface CommissionsCheckOutcome {
    /** True si el flujo redirige a comision_empresa.itx (no mostrar otros popups de Home). */
    redirectedToComisionEmpresa: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class DigoService {
    private pdv: ReplaySubject<Ipdv>;
    private renewPDV: BehaviorSubject<boolean>;
    private username: string;
    private _isCommissioned = new BehaviorSubject<boolean>(false);
    private hasCheckedCommissions = false;
    private _commissionsCheckInProgress = false;
    private _isConditionsContractSigned = new BehaviorSubject<boolean>(false);
    private hasCheckedConditionsContract = false;
    private _conditionsContractCheckInProgress = false;
    private _responseCommissions: any;
    private _responseContractConditions: any;
    private openCommissionsModalSubject = new Subject<CommissionsOpenPayload>();
    public openCommissionsModal$ = this.openCommissionsModalSubject.asObservable();
    private openContractConditionsModalSubject = new Subject<ContractConditionsOpenPayload>();
    public openContractConditionsModal$ = this.openContractConditionsModalSubject.asObservable();

    constructor(
        private auth: AuthService,
        private http: HttpClient,
        private injector: Injector,
    ) {
        this.pdv = new ReplaySubject<Ipdv>(1);
        this.renewPDV = new BehaviorSubject<boolean>(false);
    }

    public get isCommissioned(): boolean {
        return this._isCommissioned.getValue();
    }

    public set isCommissioned(val: boolean) {
        this._isCommissioned.next(val);
    }

    public get commissionsCheckInProgress(): boolean {
        return this._commissionsCheckInProgress;
    }

    public get responseCommissions(): any {
        return this._responseCommissions;
    }

    public resetCommissionsCheck(): void {
        this.hasCheckedCommissions = false;
    }

    public resolveCommissionsBlock(): void {
        this.isCommissioned = false;
        this.setRenewPDV(true);
    }

    public ensurePdvLoaded(): Observable<Ipdv> {
        return this.getPDV().pipe(
            filter((pdv): pdv is Ipdv => !!pdv?.info?.idpdv),
            take(1)
        );
    }

    public checkCommissionsOnce(pdv: Ipdv): Observable<CommissionsCheckOutcome> {
        const noRedirect: CommissionsCheckOutcome = { redirectedToComisionEmpresa: false };

        if (this.hasCheckedCommissions) {
            if (this.isCommissioned && this._responseCommissions) {
                this.openCommissionsModalSubject.next({
                    responseCommissions: this._responseCommissions,
                });
            }
            return of(noRedirect);
        }

        if (!pdv?.info?.idpdv) {
            return of(noRedirect);
        }

        this._commissionsCheckInProgress = true;

        return this.checkPdvCommisionsContract(pdv.info.idpdv).pipe(
            map((result: any): CommissionsCheckOutcome => {
                if (!result?.msg) {
                    return noRedirect;
                }

                const baseUrl = this.auth.getLegacyBaseUrl();
                if (baseUrl && baseUrl !== '' && result.msg.redirectUploadDocument) {
                    window.location.href = baseUrl + 'comision_empresa.itx';
                    return { redirectedToComisionEmpresa: true };
                }

                if (result.msg.modal === 'display') {
                    this._responseCommissions = result;
                    this.isCommissioned = true;
                    this.openCommissionsModalSubject.next({
                        responseCommissions: result,
                    });
                }

                return noRedirect;
            }),
            catchError((error) => {
                console.error('Error checking commissions contract:', error);
                return of(noRedirect);
            }),
            finalize(() => {
                this.hasCheckedCommissions = true;
                this._commissionsCheckInProgress = false;
            })
        );
    }

    private getHeaders(): HttpHeaders {
        const token = 'Bearer ' + this.auth.getToken();

        return new HttpHeaders({
            Authorization: token,
            'Content-Type': 'application/json'
        });
    }

    public getPdvFromService(): Observable<Ipdv> {
        return this.http.post<Ipdv>(environment.comunEndpoint + 'pdv/data', null, {headers: this.getHeaders()});
    }

    public setPDV(pdv: Ipdv) {
        this.username = pdv?.info?.username;
        this.pdv.next(pdv);
    }

    public getPDV(): Observable<Ipdv> {
        return this.pdv.asObservable();
    }

    // mock Orage prepago
    public getOrangeRates(pdv_id: any = '4') {
        let url = '';
        if (environment.production) {
            url = this.auth.getLegacyBaseUrl() + 'api-micros/getTarifasOrange.itx?id_pdv=' + pdv_id;
        } else {
            url = this.auth.getLegacyBaseUrl() + 'api-micros/getTarifasOrange.itx?id_pdv=' + pdv_id;
        }

        return this.http.get(url);
    }

    // mock Simyo mobile
    public getSimyoRates(pdv_id: any) {
        const url = this.auth.getLegacyBaseUrl() + 'api-micros/getTarifasSimyo.itx?id_pdv=' + pdv_id;

        return this.http.get(url);
    }

    public setRenewPDV(data) {
        this.renewPDV.next(data);
    }

    public getRenewPDV() {
        return this.renewPDV.asObservable();
    }

    public putSupportInfo(data: any) {
        return this.http.post(this.auth.getLegacyBaseUrl() + 'api-micros/pdv/update-pdv-support.itx', data);
    }

    public keepAlive(): Observable<any> {
        return this.http.get<any>(this.auth.getLegacyBaseUrl() + 'keepAlive.php', {headers: this.getHeaders()});
    }

    public goLogin() {
        // Keepalive / sesión inválida: invalidar FCM del navegador antes de redirigir.
        void this.goLoginAfterFcmCleanup();
    }

    private async goLoginAfterFcmCleanup(): Promise<void> {
        try {
            await this.injector.get(FirebaseService).clearSessionOnLogout();
        } catch {
            // No bloquear redirección a login
        }

        sessionStorage.removeItem(allSessionStorage.simyoEarthquakeCoShown);
        // Resolver antes de cualquier clear: getLegacyBaseUrl lee el JWT de localStorage
        const signinUrl = this.auth.getLegacyBaseUrl() + 'signin.itx?ac=999';
        if (!environment.production) {
            if (!location.origin.includes('localhost')) {
                window.location.href = signinUrl;
            }
        } else {
            window.location.href = signinUrl;
            // window.location.href = 'https://digo.tecalis.com/signin.itx?ac=999';
        }
    }

    public putTermsInfo(data: any) {
        return this.http.post(this.auth.getLegacyBaseUrl() + 'api-micros/pdv/update-pdv-points-terms.itx', data);
    }

    public isOpSimyoUser() {
        return this.username === 'op_simyo.pdv';
    }

    public sendCommisionsContract(data: any) {
        return this.http.get(environment.comunEndpoint + `contract/send?id_pdv=${data.idPdv}`, {headers: this.getHeaders()});
    }

    //Comprobar si pdv está entre listado de permitidos para popup de firma de contrato de comisiones. Y si han apsado 24 horas.
    public checkPdvCommisionsContract(idPdv: any) {
        return this.http.get(environment.comunEndpoint + 'contract/pdv?id_pdv='+idPdv, {headers: this.getHeaders()});
    }

    //Endpoint de mscomun para mandar logs
    public sendDataToLogs(data: any){
        const token = this.auth.getToken();
        if (!token) {
            //No se puede hacer el post sin token en los headers
            return;
        }

        const body = { log: data };
        this.http.post<any>(environment.comunEndpoint + 'logs/frontal-pdv', body, { headers: this.getHeaders() }).pipe(take(1)).subscribe();
        
    }

    public sendLogDraftOrder(order_id: any) {
        const token = this.auth.getToken();
        const info = JSON.parse(atob(token.split('.')[1]));
        const username = info.data.username;

        let params = { order_id: order_id, jwt: token, username: username };
        return this.http.post( environment.comunEndpoint + 'borrador?order_id=' + order_id + '&username=' + username + '&jwt=' + token, params, {headers: this.getHeaders()});
    }


    public getDistributorsByIds(data: any) {
        let distributors = {'distributors': data};
        return this.http.post(environment.comunEndpoint + 'distributor/info', distributors, {headers: this.getHeaders()});

    }

    public sendLogExpiredToken() {
        this.http.post( environment.comunEndpoint + 'expiredToken', {}, {headers: this.getHeaders()}).subscribe();
    }

    public get isConditionsContractSigned(): boolean {
        return this._isConditionsContractSigned.getValue();
    }

    public set isConditionsContractSigned(val: boolean) {
        this._isConditionsContractSigned.next(val);
    }

    public get conditionsContractCheckInProgress(): boolean {
        return this._conditionsContractCheckInProgress;
    }

    public get responseContractConditions(): any {
        return this._responseContractConditions;
    }

    public resetConditionsContractCheck(): void {
        this.hasCheckedConditionsContract = false;
        this.isConditionsContractSigned = false;
        this._responseContractConditions = undefined;
        this._conditionsContractCheckInProgress = false;
    }

    public resolveConditionsContractBlock(): void {
        this.isConditionsContractSigned = false;
        this.setRenewPDV(true);
    }

    public checkConditionsContractOnce(pdv: Ipdv): Observable<void> {
        if (this.hasCheckedConditionsContract) {
            if (this.isConditionsContractSigned && this._responseContractConditions) {
                this.openContractConditionsModalSubject.next({
                    responseContractConditions: this._responseContractConditions,
                });
            }
            return of(void 0);
        }

        if (!pdv?.info?.idpdv) {
            return of(void 0);
        }

        this._conditionsContractCheckInProgress = true;
        if (pdv.info.anexo_prepago_penalizaciones) {
            
            this._responseContractConditions = pdv;
            this.isConditionsContractSigned = true; // necesario para que openContractConditionsModal no haga return
            this.openContractConditionsModalSubject.next({
                responseContractConditions: pdv,
            });
        }

        return of(void 0).pipe(
            finalize(() => {
                this.hasCheckedConditionsContract = true;
                this._conditionsContractCheckInProgress = false;
            })
        );

    }

    // Comprobar si pdv está entre listado de permitidos para popup de firma de contrato de comisiones. Y si han apsado 24 horas.
    public checkPdvConditionsContract() {
        return this.http.get(environment.comunEndpoint + 'pdv/data', { headers: this.getHeaders() });
    }

    public acceptConditionsContract() {
        return this.http.post(environment.comunEndpoint + 'contract/accept-annex-conditions', {}, { headers: this.getHeaders() });
    }

}

export const commissionsGuard: CanActivateFn = () => {
    const digo = inject(DigoService);
    return !digo.isCommissioned && !digo.commissionsCheckInProgress;
};

export const conditionsContractGuard: CanActivateFn = () => {
    const digo = inject(DigoService);
    return !digo.isConditionsContractSigned && !digo.conditionsContractCheckInProgress;
};